/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { Plus, X } from "lucide-react";
import { useSamlData } from "./hooks";
import { validateSaml } from "./helpers";
import { isError } from "../../../../utils/validations";
import { setOpenToast } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { samlVersionList } from "../../../../globals/constants";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Button } from "../../../ui/button";
import { ShadcnHeading } from "../../../ui/shadcn-heading";

const Saml: React.FC = () => {
  const dispatch = useStoreDispatch();
  const { samlForm, setSamlForm, errMsg, setErrMsg, cancelHandler, submitHandler, profileAttributeName, usernameAttributeName } = useSamlData();

  /* Adapter: Radix InputField onChange(name, value) → hook state */
  const handleChangeAttributeName = (name: string, value: string) => {
    setSamlForm({ ...samlForm, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleChangeServerList = (name: string, value: string, index: number) => {
    const { authorizedServersList } = samlForm;
    let serverListToUpdate = [...authorizedServersList];
    if (authorizedServersList.hasOwnProperty(index)) {
      serverListToUpdate.splice(index, 1, value);
    } else {
      serverListToUpdate = [...serverListToUpdate, value];
    }
    setSamlForm({ ...samlForm, authorizedServersList: serverListToUpdate });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleCloseIcon = (index: number) => {
    const { authorizedServersList } = samlForm;
    let serverListToUpdate = [...authorizedServersList];
    if (authorizedServersList.hasOwnProperty(index)) {
      serverListToUpdate.splice(index, 1);
    }
    setSamlForm({ ...samlForm, authorizedServersList: serverListToUpdate });
    setErrMsg({});
  };

  const handleChangeCaEnable = (checked: boolean) => {
    setSamlForm({ ...samlForm, caEnable: checked });
  };

  const blurHandler = () => {
    const errorMessages = validateSaml(samlForm);
    setErrMsg({ ...errMsg, ...errorMessages });
  };

  const handleOnAddSAMLServer = () => {
    if (samlForm.authorizedServersList.length < 10) {
      setSamlForm({ ...samlForm, authorizedServersList: [...samlForm.authorizedServersList, ""] });
    } else {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: `Limit exeeded ${samlForm.authorizedServersList.length}` }));
    }
  };

  const handleChangeVersion = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setSamlForm({ ...samlForm, [name]: value });
  };

  const samlVersionOptions = samlVersionList.map((item) => ({ value: item.value, label: item.name }));

  const isEnableSave = !!samlForm.profileAttributeName && !!samlForm.usernameAttributeName && !isError(errMsg);

  return (

    <div
      className="flex flex-col w-full overflow-y-auto px-1 md:px-1"
      style={{ color: "var(--foreground)" }}
    >
      {/* Heading */}
      <h2 style={{ margin: 0, padding: 0, lineHeight: 0.5, marginBottom: "10px" }}>
        <ShadcnHeading className="mb-0 text-sm font-semibold text-foreground" as="span">
          SAML Attribute
        </ShadcnHeading>
      </h2>

        <div className="mt-3"
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "24px",
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--background)",
        }}
      >
        <form onSubmit={submitHandler} autoComplete="off">
          <div className="space-y-5 max-w-lg">
            {/* Profile Attribute Name */}
            <InputField
              required
              label="Profile Attribute Name"
              name={profileAttributeName}
              value={samlForm?.profileAttributeName}
              maxLength={2048}
              placeholder="Enter profile attribute name"
              onChange={handleChangeAttributeName}
              onBlur={blurHandler}
              tooltipText="Maximum length: 2048 characters"
            />


            {/* Username Attribute Name */}

            <InputField
              required
              label="Username Attribute Name"
              name={usernameAttributeName}
              value={samlForm?.usernameAttributeName}
              maxLength={2048}
              placeholder="Enter username attribute name"
              onChange={handleChangeAttributeName}
              onBlur={blurHandler}
              tooltipText="Maximum length: 2048 characters"
            />

            {/* Verify Certificate Authority checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="caEnable"
                checked={samlForm.caEnable}
                onCheckedChange={(checked) => handleChangeCaEnable(!!checked)}
                className="m-0"
              />
              <Label htmlFor={"isVerifyCertificateAuthority"} className="text-sm font-medium">
                Verify Certificate Authority
              </Label>
              ``
            </div>

            {/* SAML Version */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="samlVersion" className="text-sm font-medium mb-3">
                SAML Version
              </Label>
              <RadixSelectControl
                value={samlForm?.samlVersion || ""}
                options={samlVersionOptions}
                onValueChange={(value) => handleChangeVersion({ target: { name: "samlVersion", value } } as React.ChangeEvent<HTMLSelectElement>)}
              />
            </div>
            {/* Authorized SAML Servers */}
            <div className="flex flex-col gap-2">
              <Label htmlFor={"isAuthorizedSAMLServers"} className="text-sm font-medium">
                Authorized SAML Servers
              </Label>
            </div>

            {samlForm?.authorizedServersList?.length > 0 &&
              samlForm.authorizedServersList.map((item: string, i: number) => {
                const index = i + 1;
                return (
                  <div className="flex items-end gap-2" key={`saml-server${index}`}>
                    <div className="flex-1">
                      <InputField
                        required
                        label={`SAML Server ${index}`}
                        name={`samlServer${index}`}
                        value={item}
                        maxLength={2048}
                        placeholder={`Enter SAML server ${index}`}
                        onChange={(name: string, value: string) => handleChangeServerList(name, value, i)}
                        onBlur={blurHandler}
                        tooltipText="Maximum length: 2048 characters"
                      />
                    </div>
                    {samlForm.authorizedServersList.length > 1 && (
                      <button
                        type="button"
                        className="mb-1 flex items-center justify-center h-8 w-8 rounded-full border-0 bg-muted hover:bg-accent text-foreground"
                        onClick={() => handleCloseIcon(i)}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}

            {/* Add server button */}
            {samlForm?.authorizedServersList?.length < 10 && (
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  className="flex items-center justify-center h-8 w-8 rounded-full border-0 bg-muted hover:bg-accent text-foreground"
                  onClick={handleOnAddSAMLServer}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" type="button" onClick={cancelHandler}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isEnableSave}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </div >
    </div >
  );
};

export default Saml;
